<template>
<div class="wn-filter" :class="{ 'is-open': open }" :inert="!open">
    <div class="wn-filter__scrim" @click="$emit('close')"></div>
    <aside class="wn-filter__panel" role="dialog" :aria-label="title || $_locale('filters')">
        <div class="wn-filter__header">
            <span class="wn-filter__title">{{ title || $_locale('filters') }}</span>
            <button type="button" class="wn-round-btn" :title="$_locale('close')" @click="$emit('close')"><i class="ri-close-line"></i></button>
        </div>

        <div class="wn-filter__body">
            <div v-for="g in groups" :key="g.id" class="wn-filter__group">
                <div class="wn-filter__label">
                    {{ g.label }}
                    <span v-if="isActive(g)" class="wn-badge">{{ g.type === 'checkbox' ? draft[g.id].length : 1 }}</span>
                </div>

                <template v-if="g.type === 'daterange'">
                    <div class="wn-filter__daterange">
                        <input type="date" :aria-label="$_locale('from')" :value="draft[g.id]?.from || ''" @change="set(g.id, { from: $event.target.value, to: draft[g.id]?.to })">
                        <input type="date" :aria-label="$_locale('to')" :value="draft[g.id]?.to || ''" @change="set(g.id, { from: draft[g.id]?.from, to: $event.target.value })">
                    </div>
                    <div class="wn-filter__chips">
                        <button v-for="p in presets(g)" :key="p[0]" type="button" class="wn-chip" :class="{ 'is-on': draft[g.id]?.preset === p[0] }"
                            @click="set(g.id, draft[g.id]?.preset === p[0] ? {} : datePreset(p[0]))">{{ p[1] }}</button>
                    </div>
                </template>

                <template v-else-if="g.type === 'select'">
                    <select class="wn-filter__select" :value="draft[g.id] || ''" @change="set(g.id, $event.target.value)">
                        <option v-for="o in g.options" :key="o.value" :value="o.value">{{ o.label }}</option>
                    </select>
                </template>

                <template v-else-if="g.type === 'text'">
                    <input type="text" class="wn-filter__input" :value="draft[g.id] || ''" @input="set(g.id, $event.target.value)">
                </template>

                <template v-else-if="g.type === 'number'">
                    <div class="wn-filter__pair">
                        <select class="wn-filter__select" :value="draft[g.id]?.op || 'gt'" @change="set(g.id, { op: $event.target.value, value: draft[g.id]?.value ?? '' })">
                            <option v-for="o in numberOps" :key="o[0]" :value="o[0]">{{ $_locale(o[1]) }}</option>
                        </select>
                        <input type="number" step="any" class="wn-filter__input" :value="draft[g.id]?.value ?? ''" @input="set(g.id, { op: draft[g.id]?.op || 'gt', value: $event.target.value })">
                    </div>
                    <small v-if="isActive(g)" class="wn-filter__hint">{{ $_locale('filtering') }}: {{ $_locale(numberHints[draft[g.id].op || 'gt'], false) }} {{ draft[g.id].value }}</small>
                </template>

                <template v-else-if="g.type === 'numberrange'">
                    <div class="wn-filter__pair">
                        <input type="number" step="any" class="wn-filter__input" :placeholder="$_locale('from')" :aria-label="$_locale('from')" :value="draft[g.id]?.min ?? ''" @input="set(g.id, { min: $event.target.value, max: draft[g.id]?.max ?? '' })">
                        <input type="number" step="any" class="wn-filter__input" :placeholder="$_locale('to')" :aria-label="$_locale('to')" :value="draft[g.id]?.max ?? ''" @input="set(g.id, { min: draft[g.id]?.min ?? '', max: $event.target.value })">
                    </div>
                </template>

                <template v-else>
                    <label v-for="o in g.options" :key="o.value" class="wn-filter__option">
                        <input v-if="g.type === 'checkbox'" type="checkbox" class="form-check-input" :checked="(draft[g.id] || []).includes(o.value)" @change="toggle(g.id, o.value)">
                        <input v-else type="radio" class="form-check-input" :name="`wn-filter-${ g.id }`" :checked="(draft[g.id] || '') === o.value" @change="set(g.id, o.value)">
                        {{ o.label }}
                    </label>
                </template>
            </div>
        </div>

        <div class="wn-filter__footer">
            <button type="button" class="wn-btn wn-btn--outline" @click="draft = {}"><i class="ri-eraser-line"></i>{{ resetLabel || $_locale('clear') }}</button>
            <button type="button" class="wn-btn wn-btn--inverse" @click="apply">{{ applyLabel || $_locale('apply') }}</button>
        </div>
    </aside>
</div>
</template>

<script>
import { datePreset, isGroupActive } from './listFilters.js'

// locale keys of the comparison labels and of the "Filtering: over 1234" hint for the 'number' group type
const NUMBER_OPS = [['gt', 'bigger than'], ['gte', 'bigger than or equal to'], ['lt', 'smaller than'], ['lte', 'smaller than or equal to'], ['eq', 'equal to']];
const NUMBER_HINTS = { gt: 'over', gte: 'at least', lt: 'under', lte: 'at most', eq: 'exactly' };

export default {
    name: 'WnFilterPanel',
    props: {
        open: { type: Boolean, default: false },
        title: { type: String, default: '' },
        resetLabel: { type: String, default: '' },
        applyLabel: { type: String, default: '' },
        groups: { type: Array, required: true },
        // applied filters; the panel edits a draft and emits `apply` with the new value
        value: { type: Object, default: () => ({}) },
    },
    emits: ['apply', 'close'],
    data: () => ({ draft: {}, numberOps: NUMBER_OPS, numberHints: NUMBER_HINTS }),
    watch: {
        open: {
            immediate: true,
            handler(open) {
                if (open) this.draft = JSON.parse(JSON.stringify(this.value || {}));
            },
        },
    },
    methods: {
        datePreset,
        isActive(g) { return isGroupActive(g, this.draft[g.id]); },
        set(id, v) { this.draft = { ...this.draft, [id]: v }; },
        toggle(id, v) {
            const cur = this.draft[id] || [];
            this.set(id, cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v]);
        },
        presets(g) {
            return g.presets || [
                ['today', this.$_locale('today')],
                ['yesterday', this.$_locale('yesterday')],
                ['month', this.$_locale('this month')],
                ['lastMonth', this.$_locale('last month')],
                ['year', this.$_locale('this year')],
            ];
        },
        apply() {
            this.$emit('apply', this.draft);
            this.$emit('close');
        },
    },
}
</script>
